import {createAsyncThunk} from '@/app/store/createAsyncThunk';
import {FetcherServerError} from '@/common/services/fetcher/fetcherErrors';
import {MapPageData} from '@/app/interfaces/map';
import {hasWeatherForLocationTypes, LocationType} from '@/common/constants/locationType';

const largestSettlementsCount = 35;
const subordinatesChunkCount = 200;

const getSubLocationType = (type: LocationType) => {
	const typesOrder = [
		LocationType.Continent,
		LocationType.Country,
		LocationType.Region,
		LocationType.District,
		LocationType.SubDistrict,
		LocationType.Settlement,
	];
	const index = typesOrder.indexOf(type);
	return typesOrder[index + 1];
};

export const fetchMapsDataAction = createAsyncThunk<
	{
		language: string;
		locationId: string;
	},
	MapPageData,
	{reason: 'locationNotFound'}
>('maps/fetchMapsData', async (params, {context, success, failure}) => {
	// Fetch data
	const {backendAPI} = context;
	const {language, locationId} = params;
	let locationResponse;
	try {
		locationResponse = await backendAPI.map.getLocation(locationId, language);
	} catch (error) {
		if (error instanceof FetcherServerError && error.code === 'LOCATION_NOT_FOUND') {
			return failure({reason: 'locationNotFound'});
		}

		throw error;
	}

	const {location, superior, map, dialect} = locationResponse;

	// Disallow to show the map for settlement and sub-settlement locations
	if (hasWeatherForLocationTypes.includes(location.type)) {
		return failure({reason: 'locationNotFound'});
	}

	const relatesLocationsType =
		location.type === LocationType.SubDistrict ||
		(location.type === LocationType.District && !location.flags.parentOfSubDistrict)
			? ('settlements' as const)
			: ('subordinates' as const);

	const subLocationsType =
		relatesLocationsType === 'settlements' ? LocationType.Settlement : getSubLocationType(location.type);

	const targetCountry = location.type === LocationType.Country ? location.id : superior.country?.id;

	const [pageMetadata, subordinates, largest, continents] = await Promise.all([
		backendAPI.cachedRequest(backendAPI.misc.getPageMeta, [
			{
				locationAlternateIds: {
					locationId,
				},
				locationsStableId: targetCountry
					? {
							ids: [targetCountry],
					  }
					: undefined,
			},
		]),

		backendAPI.cachedRequest(backendAPI.location.getSubordinateLocations, [
			locationId,
			language,
			subordinatesChunkCount,
			subLocationsType,
		]),

		relatesLocationsType === 'subordinates'
			? backendAPI.cachedRequest(backendAPI.location.getLargestSettlements, [
					locationId,
					language,
					largestSettlementsCount,
					['title', 'type', 'flags'],
			  ])
			: Promise.resolve([]),

		location.type === LocationType.Continent
			? backendAPI.cachedRequest(backendAPI.location.getContinentLocations, [language])
			: Promise.resolve(undefined),
	]);

	// To avoid heavy monolithic requests, we load a potentially large list of subordinates locations
	// in groups until we load the entire list
	let lastSubordinatesChunk = subordinates;
	let lastSubordinatesItr = 0;
	while (lastSubordinatesChunk.length === subordinatesChunkCount) {
		const lastLocationId = lastSubordinatesChunk[lastSubordinatesChunk.length - 1].id;

		lastSubordinatesChunk = await backendAPI.cachedRequest(backendAPI.location.getSubordinateLocations, [
			locationId,
			language,
			subordinatesChunkCount,
			subLocationsType,
			lastLocationId,
		]);
		subordinates.push(...lastSubordinatesChunk);

		lastSubordinatesItr++;
		if (lastSubordinatesItr > 100) {
			// Emergency brake
			break;
		}
	}

	if (relatesLocationsType === 'subordinates') {
		subordinates.sort((a, b) => a.title.localeCompare(b.title));
	}

	return success({
		language,
		location,
		superior,
		dialect,
		map,
		locationMeta: {
			alternateIds: pageMetadata.locationAlternateIds,
			countryStableId: pageMetadata.locationsStableId[0],
		},
		relatesLocations:
			relatesLocationsType === 'settlements'
				? {type: 'settlements', settlements: subordinates}
				: {type: 'subordinates', largest, subordinates, continents},
	});
});
